/* eslint-disable no-param-reassign -- each message is built by overwriting its own parameter; moving ~70 of them into locals is a refactor, not lint cleanup */
import { AdditionalAttackMedication } from './types';
import {
  formatAgeFirstAttack,
  formatAuraSymptoms,
  formatFrequencyDuration,
  formatHeadacheDuration,
  formatHeadacheFrequency,
  formatPainCharacter,
  formatPainIntensity,
  formatPainLocation,
  formatPainMedicationEffect,
  formatPainMedicationEffectiveness,
  formatPainMedicationFrequency,
  formatPreferredActionDuringAttack,
  formatTriggersMessage,
} from '../../lib/helpers/formFormatters';

function formatAssociatedSymptomsMessage(associatedList: string[]): string {
  if (associatedList.length === 0) return '';
  if (associatedList[0] === 'förnekar associerade symtom') {
    return 'Förnekar associerade symtom.';
  }
  return `Associerade symptom i form av: ${associatedList.join(', ')}.`;
}

function formatHeredityMigraineMessage(heredityList: string[]): string {
  if (heredityList.length === 0) return '';
  if (heredityList[0] === 'nej') return 'Migrän finns ej i släkten.';
  return `Migrän finns hos: ${heredityList.join(', ')}.`;
}

export function transformFormData(
  diagnosedConditions: string[],
  otherHeadacheType: string,
  diagnosedConditionsDateOther: string,
  hasMultipleHeadaches: string,
  ageFirstAttack: string,
  headacheDuration: string,
  painLocation: string,
  attackPattern: string,
  painCharacter: string,
  painIntensity: string,
  physicalActivityEffect: string,
  preferredActionDuringAttack: string,
  associatedSymptoms: string[],
  auraSymptoms: string[],
  dizzinessType: string,
  dizzinessDuration: string,
  headacheFrequency: string,
  frequencyDuration: string,
  initialFrequency: string,
  worseningOverTime: string,
  worseningDuration: string,
  triggers: string[],
  comorbidities: string[],
  headacheFreeDays: string,
  nonMedicationTreatments: string[],
  medicationOveruse: string,
  hasWithdrawal: string,
  radiologyExam: string,
  controllMrt: string,
  takesPainMedication: string,
  heredityMigraine: string[],
  neurologicalFamilyDiseases: string,
  otherConditions: string,
  hypertension: string,
  weight: string,
  height: string,
  biologicalSex: string,
  pregnant: string,
  additionalInfo: string,
  additionalInfoText: string,
  massageEffect: string,
  naprapatEffect: string,
  acupunctureEffect: string,
  tensEffect: string,
  yogaEffect: string,
  bettskenEffect: string,
  overuseDuration: string,
  withdrawalDate: string,
  withdrawalEffect: string,
  radiologyDate: string,
  radiologyLocation: string,
  radiologyResult: string,
  describeAnomaly: string,
  painMedicationName: string,
  painMedicationDose: string,
  painMedicationEffect: string,
  painMedicationEffectiveness: string,
  painMedicationFrequency: string,
  pregnancyPlan: string,
  contraceptiveUse: string,
  contraceptiveType: string,
  hasPreviousPainMedication: string,
  diagnosedConditionsDateMigran: string,
  diagnosisConditionsData: string,
  diagnosisConditionsDataSpanningshuvudvark: string,
  diagnosedConditionsDateSpanningshuvudvark: string,
  diagnosisConditionsDataHorton: string,
  diagnosedConditionsDateHorton: string,
  attackPatternDuring: string,
  mrtDate: string,
  mrtLocation: string,
  mrtResult: string,
  hasAdditionalAttackMedication: string,
  neurologicalFamilyDiagnosis: string,
  neurologicalFamilyRelation: string,
  overusedMedication: string,
  hasOtherPreviousPainMedicationStopSecond: string,
  hasOtherPreviousPainMedicationStop: string,
  previousMedicationName: string,
  previousMedicationNameSecond: string,
  previousMedicationDose: string,
  previousMedicationDoseSecond: string,
  previousMedicationFrequency: string,
  previousMedicationFrequencySecond: string,
  previousMedicationStartYear: string,
  previousMedicationStartYearSecond: string,
  previousMedicationStopReason: string,
  previousMedicationStopReasonSecond: string,
  previousMedicationStopYear: string,
  otherPreventiveMedicationOtherNo: string,
  otherPreventiveMedicationOtherNoWitch: string,
  otherPreventiveMedicationOtherNoWhatReaktion: string,
  otherAlergy: string,
  previousMedicationOtherReason: string,
  previousMedicationSideEffect: string,
  previousMedicationOtherReasonTwo: string,
  preventiveMedicationHeadic: string,
  preventiveMedicationNameHeadic: string,
  preventiveMedicationDoseHeadic: string,
  preventiveMedicationEffectivenessHeadic: string,
  preventiveMedicationStartYearHeadic: string,
  preventiveMedicationSideEffectsHeadic: string,
  preventiveMedicationSideEffectDetailsHeadic: string,
  hasAdditionalPreventiveMedicationHeadic: string,
  updateMedicationTwo: string,
  doseTwo: string,
  additionalPainMedicationFrequencyTwo: string,
  previousMedicationStopYearTwo: string,
  previousMedicationSideEffectSkal: string,
  previousMedicationSideEffectBiverkan: string,
  previousMedicationSideEffectBiverkanSecond: string,
  previousMedicationSideEffectSkalSecond: string,
  previousMedicationStopYearSecond: string,
  hasOtherPreviousPainMedicationStopMedication: string,
  additionalRadiologyInfo: string,
  additionalAttackMedicine: AdditionalAttackMedication[],
  bloodPressureValue: string,
  bloodPressureLastYear: string,
  otherConditionDetails: string,
  hasPreviousPreventiveMedication: string,
  otherPreventiveMedicationExept: string,
  otherPreventiveMedicationOther: string,
  otherPreventiveMedicationOtherOne: string,
  otherPreventiveMedicationOtherTwo: string,
  otherPreventiveMedicationOtherThree: string,
  otherPreventiveMedicationNameOne: string,
  otherPreventiveMedicationNameTwo: string,
  otherPreventiveMedicationNameThree: string,
  otherPreventiveMedicationName: string,
  otherPreventiveMedication: string
) {
  if (attackPatternDuring) {
    if (attackPatternDuring === 'yes') {
      attackPatternDuring =
        'under perioder av veckor-månader, sedan besvärsfrihet under månader-år innan ny period börjar.';
    } else {
      attackPatternDuring =
        'kontinuerligt sedan minst ett år tillbaka, möjligen med uppehåll under mindre än tre månader.';
    }
  }

  let diagnosedConditionsMessage: string | undefined;

  if (Array.isArray(diagnosedConditions) && diagnosedConditions.length > 0) {
    diagnosedConditionsMessage = 'Patienten uppger sig sedan tidigare ';

    diagnosedConditions.forEach((condition) => {
      if (condition === 'Migrän') {
        diagnosedConditionsMessage += 'vara diagnosticerad med migrän.';
      } else if (condition === 'Spänningshuvudvärk') {
        diagnosedConditionsMessage +=
          'vara diagnosticerad med spänningshuvudvärk.';
      } else if (condition === 'Horton') {
        diagnosedConditionsMessage += 'vara diagnosticerad med horton.';
      } else if (condition === 'Annan huvudvärk') {
        diagnosedConditionsMessage += `vara diagnosticerad med annan huvudvärk. Typ samt år för diagnos: ${
          otherHeadacheType
        }`;
      } else if (condition === 'Nej') {
        diagnosedConditionsMessage +=
          'ej vara diagnosticerad med huvudvärkssjukdom.';
      }
    });
  }

  if (hasMultipleHeadaches) {
    if (hasMultipleHeadaches === 'yes') {
      hasMultipleHeadaches = 'Patient med flera typer av huvudvärk.';
    } else {
      hasMultipleHeadaches = 'Patient med en typ av huvudvärk.';
    }
  }

  ageFirstAttack = formatAgeFirstAttack(ageFirstAttack);
  headacheDuration = formatHeadacheDuration(headacheDuration);
  painLocation = formatPainLocation(painLocation);

  if (attackPattern === 'yes') {
    attackPattern = 'Attacker minst varannan dag upp till 8 ggr dagligen';
  } else if (attackPattern === 'no') {
    attackPattern = 'Inget clustermönster.';
  }

  if (neurologicalFamilyDiseases === 'yes') {
    neurologicalFamilyDiseases = 'Annan neurologisk sjukdom i släkten:';
  } else if (neurologicalFamilyDiseases === 'no') {
    neurologicalFamilyDiseases = 'Annan neurologisk sjukdom i släkten finns ej';
  }

  painCharacter = formatPainCharacter(painCharacter);
  painIntensity = formatPainIntensity(painIntensity);

  if (physicalActivityEffect === 'yes') {
    physicalActivityEffect =
      'Pågående huvudvärk förvärras av normal fysisk aktivitet.';
  } else if (physicalActivityEffect === 'no') {
    physicalActivityEffect =
      'Pågående huvudvärk förvärras inte av normal fysisk aktivitet.';
  }

  preferredActionDuringAttack = formatPreferredActionDuringAttack(
    preferredActionDuringAttack
  );
  if (
    preferredActionDuringAttack === 'Lägger mig ned i ett mörkt och tyst rum'
  ) {
    preferredActionDuringAttack =
      'Lägger sig ned i ett mörkt och tyst rum under pågående anfall, om möjlighet finns.';
  } else if (preferredActionDuringAttack === 'Tar en promenad') {
    preferredActionDuringAttack =
      'Tar en promenad under pågående anfall, om möjlighet finns.';
  } else if (
    preferredActionDuringAttack === 'Sätter mig ned och tar det lugnt'
  ) {
    preferredActionDuringAttack =
      'Sätter sig ned och tar det lugnt under pågående anfall, om möjlighet finns.';
  } else if (
    preferredActionDuringAttack ===
    'Går omkring då smärtan är svår att stå ut med eller förvärras i liggande'
  ) {
    preferredActionDuringAttack =
      'Går omkring då smärtan är svår att stå ut med eller förvärras i liggande under pågående anfall, om möjlighet finns.';
  }

  let associatedList: string[] = [];
  associatedSymptoms.forEach((condition) => {
    switch (condition) {
      case 'illamående/kräkningar':
        associatedList.push('illamående/kräkningar');
        break;
      case 'överkänslighet för ljus':
        associatedList.push('överkänslighet för ljus');
        break;
      case 'överkänslighet för ljud':
        associatedList.push('överkänslighet för ljud');
        break;
      case 'överkänslighet för lukter':
        associatedList.push('överkänslighet för lukter');
        break;
      case 'Nej, inget av ovanstående':
        associatedList = ['förnekar associerade symtom'];
        break;
      default:
        break;
    }
  });

  const associatedSymptomsMessage =
    formatAssociatedSymptomsMessage(associatedList);

  const auraSymptomsMessage = formatAuraSymptoms(auraSymptoms);

  if (dizzinessType === 'snurrande som när man åker karusell') {
    dizzinessType = 'Yrsel beskrivs som snurrande som när man åker karusell.';
  } else if (dizzinessType === 'gungande som på en båt') {
    dizzinessType = 'Yrsel beskrivs som gungande som på en båt.';
  } else if (dizzinessType === 'allmän olustkänsla') {
    dizzinessType = 'Yrsel beskrivs som allmän olustkänsla.';
  } else if (dizzinessType === 'inget av ovanstående') {
    dizzinessType = 'Yrsel beskrivs som inget av ovanstående.';
  }

  if (dizzinessDuration === 'mindre än 5 min') {
    dizzinessDuration = 'Uppger mindre än 5 min duration av nämnda symtom.';
  } else if (dizzinessDuration === '5-15 min') {
    dizzinessDuration = 'Uppger 5-15 min duration av nämnda symtom.';
  } else if (dizzinessDuration === '15-60 min') {
    dizzinessDuration = 'Uppger 15-60 min duration av nämnda symtom.';
  } else if (dizzinessDuration === '1-4 timmar') {
    dizzinessDuration = 'Uppger 1-4 timmar duration av nämnda symtom.';
  } else if (dizzinessDuration === 'mer än 4 timmar') {
    dizzinessDuration = 'Uppger mer än 4 timmars duration av nämnda symtom.';
  }

  headacheFrequency = formatHeadacheFrequency(headacheFrequency);
  frequencyDuration = formatFrequencyDuration(frequencyDuration);

  if (initialFrequency === 'mindre än en gång per år') {
    initialFrequency =
      'De första åren efter debuten var huvudvärksfrekvensen mindre än en gång per år.';
  } else if (initialFrequency === '1-11 gånger per år') {
    initialFrequency =
      'De första åren efter debuten var huvudvärksfrekvensen 1-11 gånger per år.';
  } else if (initialFrequency === '1-3 ggr per månad') {
    initialFrequency =
      'De första åren efter debuten var huvudvärksfrekvensen 1-3 ggr per månad.';
  } else if (initialFrequency === '4-7 ggr per månad') {
    initialFrequency =
      'De första åren efter debuten var huvudvärksfrekvensen 4-7 ggr per månad.';
  } else if (initialFrequency === '8-14 ggr per månad') {
    initialFrequency =
      'De första åren efter debuten var huvudvärksfrekvensen 8-14 ggr per månad.';
  } else if (initialFrequency === '15-29 ggr per månad') {
    initialFrequency =
      'De första åren efter debuten var huvudvärksfrekvensen 15-29 ggr per månad.';
  } else if (initialFrequency === 'dagligen') {
    initialFrequency =
      'De första åren efter debuten var huvudvärksfrekvensen dagligen.';
  }

  if (worseningOverTime === 'yes') {
    worseningOverTime = 'Anfallsfrekvensen har försämrats med tiden.';
  } else if (worseningOverTime === 'no') {
    worseningOverTime = 'Anfallsfrekvensen har varit oförändrad sedan debuten.';
  }

  if (worseningDuration === 'mindre än 1 månad') {
    worseningDuration = 'Anfallsfrekvensen har ökat sedan mindre än 1 månad.';
  } else if (worseningDuration === '1-3 månader') {
    worseningDuration = 'Anfallsfrekvensen har ökat sedan 1-3 månader.';
  } else if (worseningDuration === '3-6 månader') {
    worseningDuration = 'Anfallsfrekvensen har ökat sedan 3-6 månader.';
  } else if (worseningDuration === '6-12 månader') {
    worseningDuration = 'Anfallsfrekvensen har ökat sedan 6-12 månader.';
  } else if (worseningDuration === '1-2 år') {
    worseningDuration = 'Anfallsfrekvensen har ökat sedan 1-2 år.';
  } else if (worseningDuration === '2-5 år') {
    worseningDuration = 'Anfallsfrekvensen har ökat sedan 2-5 år.';
  } else if (worseningDuration === '5-10 år') {
    worseningDuration = 'Anfallsfrekvensen har ökat sedan 5-10 år.';
  } else if (worseningDuration === '10-20 år') {
    worseningDuration = 'Anfallsfrekvensen har ökat sedan 10-20 år.';
  } else if (worseningDuration === 'mer än 20 år') {
    worseningDuration = 'Anfallsfrekvensen har ökat sedan mer än 20 år.';
  }

  const triggersMessage = formatTriggersMessage(triggers);

  if (headacheFreeDays === '30') {
    headacheFreeDays =
      'Uppskattar att det förekommer 30 helt huvudvärksfria dagar per månad.';
  } else if (headacheFreeDays === '15-30') {
    headacheFreeDays =
      'Uppskattar att det förekommer 15-30 helt huvudvärksfria dagar per månad.';
  } else if (headacheFreeDays === '8-14') {
    headacheFreeDays =
      'Uppskattar att det förekommer 8-14 helt huvudvärksfria dagar per månad.';
  } else if (headacheFreeDays === '3-7') {
    headacheFreeDays =
      'Uppskattar att det förekommer 3-7 helt huvudvärksfria dagar per månad.';
  } else if (headacheFreeDays === '1-2') {
    headacheFreeDays =
      'Uppskattar att det förekommer 1-2 helt huvudvärksfria dagar per månad.';
  } else if (headacheFreeDays === 'inga') {
    headacheFreeDays =
      'Uppskattar att det inte förekommer någon helt huvudvärksfri dag per månad.';
  }

  let comorbiditiesMessage: string | undefined;
  const list: string[] = [];
  if (comorbidities.includes('nej, inget av ovanstående')) {
    list.push('inga övriga tillstånd');
  } else if (Array.isArray(comorbidities) && comorbidities.length > 0) {
    comorbidities.forEach((condition) => {
      switch (condition) {
        case 'nedstämdhet':
          list.push('nedstämdhet');
          break;
        case 'ångest':
          list.push('ångest');
          break;
        case 'sömnrubbning':
          list.push('sömnrubbning');
          break;
        case 'bruxism (tandgnissling)':
          list.push('bruxism');
          break;
        case 'rikliga snarkningar nattetid':
          list.push('rikliga snarkningar nattetid');
          break;
        default:
          break;
      }
    });
  }
  if (list.length > 0) {
    comorbiditiesMessage = `Bejakar ${list.join(', ')}.`;
  }

  let nonMedicationTreatmentsMessage: string | undefined;

  if (
    Array.isArray(nonMedicationTreatments) &&
    nonMedicationTreatments.length > 0
  ) {
    if (nonMedicationTreatments.includes('inga av ovanstående')) {
      nonMedicationTreatmentsMessage =
        'Har ej provat icke medikamentella behandlingar.';
    } else {
      const treatments = nonMedicationTreatments.filter(
        (t) => t !== 'inga av ovanstående'
      );
      nonMedicationTreatmentsMessage = `Har av icke medikamentella behandlingar provat: ${treatments.join(
        ', '
      )}.`;
    }
  }

  if (massageEffect === 'Massage med mycket god effekt.') {
    massageEffect = 'Mycket god';
  } else if (massageEffect === 'Massage med god effekt.') {
    massageEffect = 'God';
  } else if (massageEffect === 'Massage med måttlig effekt.') {
    massageEffect = 'Måttlig';
  } else if (massageEffect === 'Massage med tveksam effekt.') {
    massageEffect = 'Tveksam';
  } else if (massageEffect === 'Massage utan effekt.') {
    massageEffect = 'Ingen';
  }

  if (naprapatEffect !== undefined) {
    if (
      naprapatEffect ===
      'Naprapat/kiropraktor behandling med mycket god effekt.'
    ) {
      naprapatEffect = 'Mycket god';
    } else if (
      naprapatEffect === 'Naprapat/kiropraktor behandling med god effekt.'
    ) {
      naprapatEffect = 'God';
    } else if (
      naprapatEffect === 'Naprapat/kiropraktor behandling med måttlig effekt.'
    ) {
      naprapatEffect = 'Måttlig';
    } else if (
      naprapatEffect === 'Naprapat/kiropraktor behandling med tveksam effekt.'
    ) {
      naprapatEffect = 'Tveksam';
    } else if (
      naprapatEffect === 'Naprapat/kiropraktor behandling utan effekt.'
    ) {
      naprapatEffect = 'Ingen';
    }
  }

  if (acupunctureEffect === 'Akupunktur med mycket god effekt.') {
    acupunctureEffect = 'Mycket god';
  } else if (acupunctureEffect === 'Akupunktur med god effekt.') {
    acupunctureEffect = 'God';
  } else if (acupunctureEffect === 'Akupunktur med måttlig effekt.') {
    acupunctureEffect = 'Måttlig';
  } else if (acupunctureEffect === 'Akupunktur med tveksam effekt.') {
    acupunctureEffect = 'Tveksam';
  } else if (acupunctureEffect === 'Akupunktur utan effekt.') {
    acupunctureEffect = 'Ingen';
  }

  if (tensEffect === 'TENS med mycket god effekt.') {
    tensEffect = 'Mycket god';
  } else if (tensEffect === 'TENS med god effekt.') {
    tensEffect = 'God';
  } else if (tensEffect === 'TENS med måttlig effekt.') {
    tensEffect = 'Måttlig';
  } else if (tensEffect === 'TENS med tveksam effekt.') {
    tensEffect = 'Tveksam';
  } else if (tensEffect === 'TENS utan effekt.') {
    tensEffect = 'Ingen';
  }

  if (
    yogaEffect === 'Yoga eller annan avslappningsmetod med mycket god effekt.'
  ) {
    yogaEffect = 'Mycket god';
  } else if (
    yogaEffect === 'Yoga eller annan avslappningsmetod med god effekt.'
  ) {
    yogaEffect = 'God';
  } else if (
    yogaEffect === 'Yoga eller annan avslappningsmetod med måttlig effekt.'
  ) {
    yogaEffect = 'Måttlig';
  } else if (
    yogaEffect === 'Yoga eller annan avslappningsmetod med tveksam effekt.'
  ) {
    yogaEffect = 'Tveksam';
  } else if (yogaEffect === 'Yoga eller annan avslappningsmetod utan effekt.') {
    yogaEffect = 'Ingen';
  }

  if (bettskenEffect === 'Bettskena med mycket god effekt.') {
    bettskenEffect = 'Mycket god';
  } else if (bettskenEffect === 'Bettskena med god effekt.') {
    bettskenEffect = 'God';
  } else if (bettskenEffect === 'Bettskena med måttlig effekt.') {
    bettskenEffect = 'Måttlig';
  } else if (bettskenEffect === 'Bettskena med tveksam effekt.') {
    bettskenEffect = 'Tveksam';
  } else if (bettskenEffect === 'Bettskena utan effekt.') {
    bettskenEffect = 'Ingen';
  }

  if (overuseDuration === 'mindre än 1 månad') {
    overuseDuration = 'Överkonsumtion sedan mindre än 1 månad.';
  } else if (overuseDuration === '1-3 månader') {
    overuseDuration = 'Överkonsumtion sedan 1-3 månader.';
  } else if (overuseDuration === '3-6 månader') {
    overuseDuration = 'Överkonsumtion sedan 3-6 månader.';
  } else if (overuseDuration === '6-12 månader') {
    overuseDuration = 'Överkonsumtion sedan 6-12 månader.';
  } else if (overuseDuration === '1-2 år') {
    overuseDuration = 'Överkonsumtion sedan 1-2 år.';
  } else if (overuseDuration === '2-5 år') {
    overuseDuration = 'Överkonsumtion sedan 2-5 år.';
  } else if (overuseDuration === '5-10 år') {
    overuseDuration = 'Överkonsumtion sedan 5-10 år.';
  } else if (overuseDuration === '10-20 år') {
    overuseDuration = 'Överkonsumtion sedan 10-20 år.';
  } else if (overuseDuration === 'mer än 20 år') {
    overuseDuration = 'Överkonsumtion sedan mer än 20 år.';
  } else if (overuseDuration === 'sedan debuten') {
    overuseDuration = 'Överkonsumtion sedan debuten.';
  }

  if (withdrawalEffect === 'Klart minskad huvudvärk') {
    withdrawalEffect = 'med minskad huvudvärk som följd.';
  } else if (withdrawalEffect === 'Måttligt minskad huvudvärk') {
    withdrawalEffect = 'med måttligt minskad huvudvärk som följd.';
  } else if (withdrawalEffect === 'En aning minskad huvudvärk') {
    withdrawalEffect = 'med en aning minskad huvudvärk som följd.';
  } else if (withdrawalEffect === 'Ingen skillnad') {
    withdrawalEffect = 'utan förbättring.';
  }

  if (radiologyResult === 'normalt resultat') {
    radiologyResult = 'Undersökning med normalt resultat.';
  } else if (
    radiologyResult === 'väsentligen normalt med lättare/obetydlig anmärkning'
  ) {
    radiologyResult =
      'Undersökning med väsentligen normalt med lättare/obetydlig anmärkning.';
  } else if (radiologyResult === 'onormalt fynd') {
    radiologyResult = 'Undersökning med onormalt fynd i form av';
  }

  if (mrtResult === 'normalt resultat') {
    mrtResult = 'Undersökning med normalt resultat.';
  } else if (
    mrtResult === 'väsentligen normalt med lättare/obetydlig anmärkning'
  ) {
    mrtResult =
      'Undersökning med väsentligen normalt med lättare/obetydlig anmärkning.';
  } else if (mrtResult === 'onormalt fynd') {
    mrtResult = 'Undersökning med onormalt fynd i form av';
  }

  if (medicationOveruse === 'yes') {
    medicationOveruse = 'Bejakar överkonsumtion av analgetika. Överkonsumerar';
  } else if (medicationOveruse === 'no') {
    medicationOveruse = 'Nekar överkonsumtion av analgetika.';
  }

  if (hasWithdrawal === 'yes') {
    hasWithdrawal = 'Har genomgått läkemedelsavgiftning';
  } else if (hasWithdrawal === 'no') {
    hasWithdrawal = 'Läkemedelsavgiftning har ej genomförts.';
  }

  if (radiologyExam === 'yes') {
    radiologyExam =
      'Har genomgått skiktröntgen (datortomografi, CT, DT) av huvudet';
  } else if (radiologyExam === 'no') {
    radiologyExam = 'Har ej genomgått skiktröntgen.';
  }

  if (controllMrt === 'yes') {
    controllMrt = 'Har genomgått magnetkameraundersökning (MRT) av huvudet';
  } else if (controllMrt === 'no') {
    controllMrt = 'Har ej genomgått magnetkameraundersökning (MRT) av huvudet.';
  }

  if (takesPainMedication === 'yes') {
    takesPainMedication = 'Vid anfall tas (1)';
  } else if (takesPainMedication === 'no') {
    takesPainMedication = 'Vid anfall tas ingen anfallsmedicin.';
  }

  if (preventiveMedicationHeadic === 'yes') {
    preventiveMedicationHeadic = 'Som profylax tas (1)';
  } else if (preventiveMedicationHeadic === 'no') {
    preventiveMedicationHeadic = 'Tar ej profylax';
  }

  let heredityList: string[] = [];
  heredityMigraine.forEach((condition) => {
    switch (condition) {
      case 'far':
        heredityList.push('far');
        break;
      case 'mor':
        heredityList.push('mor');
        break;
      case 'syskon':
        heredityList.push('syskon');
        break;
      case 'far- eller morförälder':
        heredityList.push('far- eller morförälder');
        break;
      case 'faster/moster/farbror/morbror':
        heredityList.push('faster/moster/farbror/morbror');
        break;
      case 'kusin':
        heredityList.push('kusin');
        break;
      case 'nej':
        heredityList = ['nej'];
        break;
      default:
        break;
    }
  });

  const heredityMigraineMessage = formatHeredityMigraineMessage(heredityList);

  if (otherConditions === 'yes') {
    otherConditions = 'Har en känd';
  } else if (otherConditions === 'no') {
    otherConditions = 'Nekar andra sjukdomar.';
  }

  if (hypertension === 'yes') {
    hypertension = 'Uppger känd hypertoni.';
  } else if (hypertension === 'no') {
    hypertension = 'Nekar känd hypertoni.';
  }

  function calculateBMI(weightKg: number, heightM: number): number {
    if (heightM <= 0) return 0;
    return weightKg / (heightM * heightM);
  }

  const bmi = calculateBMI(parseFloat(weight), parseFloat(height) / 100);

  if (biologicalSex === 'female') {
    biologicalSex = 'Kvinna';
  } else if (biologicalSex === 'male') {
    biologicalSex = 'Man';
  }

  if (pregnant === 'yes') {
    pregnant = 'OBS! Är gravid.';
  } else if (pregnant === 'no') {
    pregnant = 'Ej gravid';
  }

  if (additionalInfo === 'yes') {
    additionalInfo = `Patienten tillägger att ${additionalInfoText?.trim()}`;
  } else if (additionalInfo === 'no') {
    additionalInfo = 'Patienten önskar inte tillägga något övrigt.';
  }

  painMedicationEffect = formatPainMedicationEffect(painMedicationEffect);
  painMedicationEffectiveness = formatPainMedicationEffectiveness(
    painMedicationEffectiveness
  );
  painMedicationFrequency = formatPainMedicationFrequency(
    painMedicationFrequency
  );

  if (pregnancyPlan === 'yes') {
    pregnancyPlan = 'OBS! Planerar graviditet inom 6 månader.';
  } else if (pregnancyPlan === 'no') {
    pregnancyPlan = 'Planerar ej graviditet';
  }

  if (contraceptiveUse === 'yes') {
    contraceptiveUse = 'Preventivmedel.';
  } else if (contraceptiveUse === 'no') {
    contraceptiveUse = 'no';
  }

  if (contraceptiveType === 'p-piller') {
    contraceptiveType = 'p-piller (för in i akt med-lista).';
  } else if (contraceptiveType === 'hormonspiral') {
    contraceptiveType = 'hormonspiral.';
  } else if (contraceptiveType === 'kopparspiral') {
    contraceptiveType = 'kopparspiral.';
  } else if (contraceptiveType === 'p-stav') {
    contraceptiveType = 'p-stav.';
  } else if (contraceptiveType === 'p-spruta') {
    contraceptiveType = 'p-spruta.';
  } else if (contraceptiveType === 'kondom') {
    contraceptiveType = 'kondom.';
  } else if (contraceptiveType === 'annat') {
    contraceptiveType = 'annat.';
  }

  const data = {
    diagnosedConditions: diagnosedConditionsMessage,
    hasMultipleHeadaches,
    headacheDuration,
    ageFirstAttack,
    bloodPressureValue,
    bloodPressureLastYear,
    otherConditionDetails,
    hasPreviousPreventiveMedication,
    otherPreventiveMedicationExept,
    painLocation,
    otherPreventiveMedicationNameOne,
    otherPreventiveMedicationNameTwo,
    otherPreventiveMedicationNameThree,
    attackPattern,
    painCharacter,
    painIntensity,
    physicalActivityEffect,
    preferredActionDuringAttack,
    associatedSymptoms: associatedSymptomsMessage,
    auraSymptoms: auraSymptomsMessage,
    dizzinessType,
    dizzinessDuration,
    headacheFrequency,
    initialFrequency,
    worseningOverTime,
    worseningDuration,
    triggers: triggersMessage,
    headacheFreeDays,
    comorbidities: comorbiditiesMessage,
    nonMedicationTreatments: nonMedicationTreatmentsMessage,
    massageEffect,
    acupunctureEffect,
    naprapatEffect,
    tensEffect,
    yogaEffect,
    bettskenEffect,
    radiologyExam,
    overuseDuration,
    withdrawalDate,
    radiologyDate,
    withdrawalEffect,
    radiologyLocation,
    frequencyDuration,
    medicationOveruse,
    hasWithdrawal,
    controllMrt,
    takesPainMedication,
    heredityMigraine: heredityMigraineMessage,
    neurologicalFamilyDiseases,
    otherConditions,
    hypertension,
    weight,
    height,
    bmi,
    biologicalSex,
    pregnant,
    additionalInfo,
    describeAnomaly,
    radiologyResult,
    painMedicationName,
    painMedicationDose,
    painMedicationEffect,
    painMedicationFrequency,
    painMedicationEffectiveness,
    pregnancyPlan,
    contraceptiveUse,
    additionalRadiologyInfo,
    contraceptiveType,
    hasPreviousPainMedication,
    diagnosedConditionsDateMigran,
    diagnosisConditionsData,
    diagnosisConditionsDataSpanningshuvudvark,
    diagnosedConditionsDateSpanningshuvudvark,
    diagnosisConditionsDataHorton,
    diagnosedConditionsDateHorton,
    attackPatternDuring,
    mrtDate,
    mrtLocation,
    mrtResult,
    hasAdditionalAttackMedication,
    neurologicalFamilyDiagnosis,
    neurologicalFamilyRelation,
    overusedMedication,
    hasOtherPreviousPainMedicationStopSecond,
    hasOtherPreviousPainMedicationStop,
    previousMedicationName,
    previousMedicationNameSecond,
    previousMedicationDose,
    previousMedicationDoseSecond,
    previousMedicationFrequency,
    previousMedicationFrequencySecond,
    previousMedicationStartYear,
    previousMedicationStartYearSecond,
    previousMedicationStopReason,
    previousMedicationStopReasonSecond,
    previousMedicationStopYear,
    previousMedicationSideEffect,
    otherPreventiveMedicationOtherNo,
    otherPreventiveMedicationOtherNoWitch,
    otherPreventiveMedicationOtherNoWhatReaktion,
    otherAlergy,
    otherHeadacheType,
    diagnosedConditionsDateOther,
    previousMedicationOtherReason,
    previousMedicationOtherReasonTwo,
    preventiveMedicationHeadic,
    preventiveMedicationNameHeadic,
    preventiveMedicationDoseHeadic,
    preventiveMedicationEffectivenessHeadic,
    preventiveMedicationStartYearHeadic,
    preventiveMedicationSideEffectsHeadic,
    preventiveMedicationSideEffectDetailsHeadic,
    hasAdditionalPreventiveMedicationHeadic,
    updateMedicationTwo,
    doseTwo,
    additionalPainMedicationFrequencyTwo,
    previousMedicationStopYearTwo,
    previousMedicationSideEffectSkal,
    previousMedicationSideEffectBiverkan,
    previousMedicationSideEffectBiverkanSecond,
    previousMedicationSideEffectSkalSecond,
    previousMedicationStopYearSecond,
    hasOtherPreviousPainMedicationStopMedication,
    otherPreventiveMedicationOther,
    otherPreventiveMedicationOtherOne,
    otherPreventiveMedicationOtherTwo,
    otherPreventiveMedicationOtherThree,
    otherPreventiveMedicationName,
    otherPreventiveMedication,
    additionalInfoText,
  };

  return data;
}
